import "./osc-module.css";

import { Knob } from "./knob.ts";
import { ToggleSwitch } from "./toggle-switch.ts";
import { MorphStrip } from "./morph-strip.ts";
import { MORPH_MIN, MORPH_MAX } from "./morph-shared.ts";

// OscModule — one oscillator's controls as a self-contained module.
// Layout (top → bottom):
//   [OSC N · ......... enable]
//   [ MorphStrip (compact) — preview, anchors, slider ]
//   [ OCT  ·  DETUNE  ·  LEVEL ]
//
// Each control fires the appropriate engine param path on change. Param
// paths follow the engine's "osc{1|2}.{shape|octave|detune_cents|level}"
// convention (and "osc{1|2}.enabled" via the discrete setter).

export interface OscState {
  shape: number;
  octave: number;
  detuneCents: number;
  level: number;
  enabled: boolean;
}

export interface OscModuleOptions {
  container: HTMLElement;
  /** Oscillator index, 1 or 2. Drives the title and the engine paths. */
  index: 1 | 2;
  /** Engine adapter — invoked when controls change. Two flavours because
   *  the engine's wasm bridge has separate paths for continuous floats
   *  and discrete ints/bools. */
  setParam: (path: string, value: number) => void;
  setDiscrete: (path: string, value: number) => void;
  /** Initial parameter snapshot. Lets the UI start at the engine's
   *  defaults and stay in sync if the engine changes them later. */
  initial?: Partial<OscState>;
}

export class OscModule {
  private root: HTMLDivElement;
  private enableSwitch: ToggleSwitch;
  private morphStrip!: MorphStrip;
  private octaveKnob!: Knob;
  private detuneKnob!: Knob;
  private levelKnob!: Knob;

  constructor(opts: OscModuleOptions) {
    const init = opts.initial ?? {};
    const idx = opts.index;
    const pathBase = `osc${idx}`;

    this.root = document.createElement("div");
    this.root.className = "osc-module";

    // Header: title + enable toggle.
    const header = document.createElement("div");
    header.className = "osc-module-header";

    const title = document.createElement("span");
    title.className = "osc-module-title";
    title.textContent = `osc ${idx}`;
    header.append(title);

    const enableHost = document.createElement("div");
    enableHost.className = "osc-module-enable";
    header.append(enableHost);

    this.enableSwitch = new ToggleSwitch({
      container: enableHost,
      label: "on",
      labelPosition: "left",
      active: init.enabled ?? true,
      onToggle: (active) => {
        opts.setDiscrete(`${pathBase}.enabled`, active ? 1 : 0);
      },
    });

    this.root.append(header);

    // Morph strip — combines waveform selector (anchors) + shape slider +
    // live preview. The engine's `osc.shape` is normalized 0..1, but the
    // strip's morph is 0..3. We map between the two so the strip can show
    // four equally-spaced archetype anchors while still hitting the same
    // engine values the existing slider/etc. expected.
    const morphRow = document.createElement("div");
    morphRow.className = "osc-module-morph";
    this.root.append(morphRow);

    const initialShape01 = init.shape ?? 0;
    // Match the maxWidth of the other modules' canvases (FilterModule
     // and EnvModule both cap at 360) so all three modules read as the
     // same visual width across the panel.
    this.morphStrip = new MorphStrip({
      container: morphRow,
      compact: true,
      fluid: true,
      width: 360,
      value: initialShape01 * (MORPH_MAX - MORPH_MIN),
      onChange: (morph) => {
        const shape01 = morph / (MORPH_MAX - MORPH_MIN);
        opts.setParam(`${pathBase}.shape`, shape01);
      },
    });

    // Knob row — octave (integer), detune (cents), level (0..1).
    const knobs = document.createElement("div");
    knobs.className = "osc-module-knobs";
    this.root.append(knobs);

    this.octaveKnob = new Knob({
      container: knobs,
      size: 44,
      min: -3,
      max: 3,
      default: 0,
      value: init.octave ?? 0,
      step: 1,
      label: "oct",
      format: (v) => {
        const n = Math.round(v);
        if (n === 0) return "0";
        return n > 0 ? `+${n}` : `${n}`;
      },
      onChange: (v) => opts.setDiscrete(`${pathBase}.octave`, Math.round(v)),
    });

    this.detuneKnob = new Knob({
      container: knobs,
      size: 44,
      min: -100,
      max: 100,
      default: 0,
      value: init.detuneCents ?? 0,
      label: "detune",
      format: (v) => `${v >= 0 ? "+" : ""}${v.toFixed(0)}¢`,
      onChange: (v) => opts.setParam(`${pathBase}.detune_cents`, v),
    });

    this.levelKnob = new Knob({
      container: knobs,
      size: 44,
      min: 0,
      max: 1,
      default: idx === 1 ? 0.8 : 0,
      value: init.level ?? (idx === 1 ? 0.8 : 0),
      label: "level",
      format: (v) => v.toFixed(2),
      onChange: (v) => opts.setParam(`${pathBase}.level`, v),
    });

    opts.container.append(this.root);
  }

  getState(): OscState {
    // Morph strip is on a 0..3 scale internally; engine + preset both
    // use the 0..1 form, so divide here at the boundary.
    const morph = this.morphStrip.getValue();
    return {
      shape: morph / (MORPH_MAX - MORPH_MIN),
      octave: this.octaveKnob.current,
      detuneCents: this.detuneKnob.current,
      level: this.levelKnob.current,
      enabled: this.enableSwitch.isActive(),
    };
  }

  setState(s: OscState): void {
    this.morphStrip.setValue(s.shape * (MORPH_MAX - MORPH_MIN));
    this.octaveKnob.setValue(s.octave);
    this.detuneKnob.setValue(s.detuneCents);
    this.levelKnob.setValue(s.level);
    // setActive now fires onToggle on change, so the engine sees the
    // enable flip via the existing onToggle wiring.
    this.enableSwitch.setActive(s.enabled);
  }

  setEnabled(enabled: boolean): void {
    this.enableSwitch.setActive(enabled);
  }
}
